import React from 'react';

import { MIC, ROBOT_OFFSET_X } from './railLayout';
import { INK, INK_FAINT, INK_SOFT, PAPER } from './railPalette';

interface MicrophoneProps {
  /** Shift of the whole stand along the belt, in scene units */
  offsetX?: number;
  /** Mirror the stand so the boom reaches back over the belt from the right */
  mirrored?: boolean;
}

/**
 * ### Microphone
 *
 * A boom stand behind the belt: weighted tripod base, telescoping pole with
 * two clamp collars, a pivot with a counterweight on the back of the boom,
 * and at the front a swivel from which the shock mount and capsule hang,
 * angled on down the line of the boom over the inspection point. The cable
 * runs down the pole in a dashed line.
 *
 * Mirroring flips the drawing about the pole, so a second station can face
 * the other way further up the belt.
 */
export function Microphone({ offsetX = 0, mirrored = false }: MicrophoneProps) {
  const { poleX, poleTop, pivotY, boomEndX, boomEndY } = MIC;
  const boomBackX = poleX - 46;
  const boomBackY = pivotY - 32;
  const placement = mirrored
    ? `translate(${offsetX + 2 * poleX} 0) scale(-1 1)`
    : `translate(${offsetX} 0)`;

  return (
    <g
      transform={placement}
      fill={PAPER}
      stroke={INK}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1={poleX} y1={256} x2={poleX - 30} y2={272} strokeWidth={3} />
      <line x1={poleX} y1={256} x2={poleX + 30} y2={272} strokeWidth={3} />
      <line x1={poleX} y1={256} x2={poleX + 4} y2={280} strokeWidth={3} />
      <rect x={poleX - 9} y={236} width={18} height={22} rx={3} />

      <rect x={poleX - 4} y={170} width={8} height={70} rx={2} />
      <rect x={poleX - 3} y={poleTop} width={6} height={54} rx={2} />
      <rect
        x={poleX - 7}
        y={166}
        width={14}
        height={9}
        rx={2}
        stroke={INK_SOFT}
      />
      <rect
        x={poleX - 7}
        y={206}
        width={14}
        height={9}
        rx={2}
        stroke={INK_SOFT}
      />

      <path
        d={`M${poleX + 4},${186} C ${poleX + 30},${196} ${poleX + 34},${230} ${poleX + 14},${254}`}
        fill="none"
        stroke={INK_SOFT}
        strokeDasharray="3 4"
      />

      <line
        x1={boomBackX}
        y1={boomBackY}
        x2={boomEndX}
        y2={boomEndY}
        strokeWidth={5}
      />
      <rect
        x={boomBackX - 6}
        y={boomBackY - 12}
        width={20}
        height={22}
        rx={4}
        transform={`rotate(32 ${boomBackX} ${boomBackY})`}
      />
      <circle cx={poleX} cy={pivotY} r={7} />
      <circle cx={poleX} cy={pivotY} r={2.5} fill={INK} stroke="none" />
      <line
        x1={poleX + 7}
        y1={pivotY - 6}
        x2={poleX + 14}
        y2={pivotY - 12}
        stroke={INK_SOFT}
      />

      {/*
       * The capsule hangs from a swivel at the boom's end and is turned to
       * carry the boom's line on downward, so the two read as one arm rather
       * than a capsule floating under a stick.
       */}
      <g transform={`translate(${boomEndX} ${boomEndY}) rotate(-24)`}>
        <circle cx={0} cy={0} r={4} />
        <circle cx={0} cy={0} r={1.5} fill={INK} stroke="none" />
        <line x1={0} y1={4} x2={0} y2={14} strokeWidth={3} />
        <ellipse cx={0} cy={38} rx={16} ry={24} stroke={INK_SOFT} />
        <g stroke={INK_FAINT} fill="none">
          <line x1={-15} y1={30} x2={-8} y2={24} />
          <line x1={15} y1={30} x2={8} y2={24} />
          <line x1={-14} y1={50} x2={-8} y2={54} />
          <line x1={14} y1={50} x2={8} y2={54} />
        </g>
        <rect x={-8} y={14} width={16} height={44} rx={8} />
        <path d="M-8,28 H8" stroke={INK_SOFT} />
        <g stroke={INK_SOFT}>
          <line x1={-5} y1={36} x2={5} y2={36} />
          <line x1={-5} y1={42} x2={5} y2={42} />
          <line x1={-5} y1={48} x2={5} y2={48} />
        </g>
      </g>
    </g>
  );
}

/**
 * ### RobotArm
 *
 * The picker after the microphone: bolted base plate, pedestal with a
 * service panel, shoulder and elbow joints drawn as concentric rings, a
 * cylinder along the upper arm, and a two-finger gripper with pads. It
 * hovers clear of the belt until a defect box is under the gripper, then
 * comes down onto it, closes, and raises it off the line. The arm turns
 * about the shoulder and the gripper closes on itself; the origins are
 * fixed in the stylesheet from the joint centres drawn here, which is why
 * the whole picker is shifted into place by one outer translate rather than
 * redrawn.
 */
export function RobotArm() {
  return (
    <g
      transform={`translate(${ROBOT_OFFSET_X} 0)`}
      fill={PAPER}
      stroke={INK}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx={890} cy={262} rx={30} ry={10} />
      <ellipse cx={890} cy={262} rx={20} ry={6} stroke={INK_SOFT} />
      <circle cx={866} cy={262} r={1.6} fill={INK} stroke="none" />
      <circle cx={914} cy={262} r={1.6} fill={INK} stroke="none" />
      <rect x={876} y={196} width={28} height={66} rx={4} />
      <rect x={882} y={214} width={16} height={26} rx={2} stroke={INK_SOFT} />
      <line x1={882} y1={226} x2={898} y2={226} stroke={INK_FAINT} />

      <g className="prl-arm">
        <polygon points="881,190 899,202 838,146 822,130" />
        <line
          x1={872}
          y1={178}
          x2={846}
          y2={152}
          stroke={INK_SOFT}
          strokeWidth={4}
        />
        <line
          x1={866}
          y1={182}
          x2={840}
          y2={156}
          stroke={PAPER}
          strokeWidth={1.5}
        />
        <path
          d="M890,210 C 870,230 850,180 830,150"
          fill="none"
          stroke={INK_SOFT}
          strokeDasharray="3 4"
        />

        <g>
          <polygon points="818,138 834,138 802,236 790,232" />
          <line x1={824} y1={150} x2={798} y2={226} stroke={INK_FAINT} />
          <g className="prl-grip">
            <circle cx={796} cy={232} r={8} />
            <circle cx={796} cy={232} r={3} stroke={INK_SOFT} />
            <path
              d="M792,240 L782,254 L786,266 L792,266"
              fill="none"
              strokeWidth={3}
            />
            <path
              d="M800,240 L810,254 L806,266 L800,266"
              fill="none"
              strokeWidth={3}
            />
            <line
              x1={786}
              y1={264}
              x2={792}
              y2={264}
              stroke={INK_SOFT}
              strokeWidth={5}
            />
            <line
              x1={800}
              y1={264}
              x2={806}
              y2={264}
              stroke={INK_SOFT}
              strokeWidth={5}
            />
          </g>
        </g>
        <circle cx={826} cy={138} r={11} />
        <circle cx={826} cy={138} r={5} stroke={INK_SOFT} />
        <circle cx={826} cy={138} r={1.6} fill={INK} stroke="none" />
      </g>

      <circle cx={890} cy={196} r={14} />
      <circle cx={890} cy={196} r={7} stroke={INK_SOFT} />
      <circle cx={890} cy={196} r={2} fill={INK} stroke="none" />
    </g>
  );
}
